import type { Dispatch, ReactElement, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { DailyQuestView } from '../../../models/quest';


export interface DailyQuestCardXActionsProps {
  quest: DailyQuestView;
  t: TFunction;
  isXFollowQuest: (questId: number) => boolean;
  isXQuestOnModeration: (questId: number) => boolean;
  getXQuestModerationTimestamp: (questId: number) => number | null;
  getXQuestVerificationStartTimestamp: (questId: number) => number | null;
  clearXQuestModerationTimestamp: (questId: number) => void;
  clearXQuestVerificationStartTimestamp: (questId: number) => void;
  setActiveXModalQuestId: Dispatch<SetStateAction<number>>;
  setShowModerationInfoModal: Dispatch<SetStateAction<boolean>>;
  handleSubscribeToX: (questId: number) => void;
  handleVerifyXSubscription: (questId: number, userQuestId?: number) => Promise<void>;
}

/** Returns action button for X-follow quests, or null if not applicable / handled elsewhere. */
export default function DailyQuestCardXActions({
  quest,
  t,
  isXFollowQuest,
  isXQuestOnModeration,
  getXQuestModerationTimestamp,
  getXQuestVerificationStartTimestamp,
  clearXQuestModerationTimestamp,
  clearXQuestVerificationStartTimestamp,
  setActiveXModalQuestId,
  setShowModerationInfoModal,
  handleSubscribeToX,
  handleVerifyXSubscription,
}: DailyQuestCardXActionsProps): ReactElement | null {
if (isXFollowQuest(quest.questId)) {
  // Якщо винагорода вже забрана - не показуємо кнопку тут (буде показана в другому блоці)
  if (quest.isRewardClaimed) {
    return null;
  }

  const hasModerationTimestamp = getXQuestModerationTimestamp(quest.questId) !== null;
  const isOnModeration = isXQuestOnModeration(quest.questId);
  const verificationStartTimestamp = getXQuestVerificationStartTimestamp(quest.questId);

  // Модерація має пріоритет над isFinished — бекенд одразу позначає квест завершеним,
  // але ми показуємо кнопку модерації поки не пройде час очікування
  if (isOnModeration) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          setActiveXModalQuestId(quest.questId);
          setShowModerationInfoModal(true);
        }}
        className="w-full cursor-pointer"
      >
        <button
          disabled={true}
          className="pointer-events-none flex w-full cursor-not-allowed items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised"
        >
          {t("MODERATION")}
          <span className="text-[16px]">⏳</span>
        </button>
      </div>
    );
  }

  // Якщо бекенд вже позначив квест як завершений (але не на модерації) — не показуємо FOLLOW/VERIFY,
  // claim кнопка буде в другому блоці
  if (quest.isFinished) {
    return null;
  }

  // Якщо бекенд каже що квест НЕ виконаний, але в CloudStorage є старі timestamps
  // (наприклад, квест скинуто в БД) — скидаємо CloudStorage і показуємо FOLLOW заново
  if (hasModerationTimestamp && !isOnModeration) {
    clearXQuestModerationTimestamp(quest.questId);
    clearXQuestVerificationStartTimestamp(quest.questId);
  }

  // Показуємо "ПІДПИСАТИСЯ НА НАШ X" або "ПЕРЕВІРИТИ ПІДПИСКУ"
  if (!hasModerationTimestamp || !isOnModeration) {
    // Визначаємо чи є актуальний verification timestamp
    // (якщо ми щойно скинули moderation — verification теж скинутий, показуємо FOLLOW)
    const hasActiveVerification = verificationStartTimestamp && !(hasModerationTimestamp && !isOnModeration);
    if (!hasActiveVerification) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleSubscribeToX(quest.questId);
          }}
          className="flex w-full cursor-pointer items-center justify-center gap-md rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised"
        >
          <img
            src="/ui/daily-quests/x-logo.png"
            alt="X"
            className="h-5 w-5 object-contain"
          />
          {t("FOLLOW")}
        </button>
      );
    }

    // Якщо є timestamp перевірки (вже було натискання на підписку) - показуємо "ПЕРЕВІРИТИ ПІДПИСКУ"
    if (hasActiveVerification) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleVerifyXSubscription(quest.questId, quest.userQuestId);
          }}
          className="flex w-full cursor-pointer items-center justify-center rounded-[34.925px] border border-accent bg-white px-3xl py-sm [font-family:Lato,sans-serif] text-[14px] font-bold text-accent shadow-card-raised"
        >
          {t("VERIFY")}
        </button>
      );
    }
  }

  // Якщо є timestamp модерації але не на модерації (мінуло 1+ годину) - квест має показувати claim кнопку
  // Ця логіка буде оброблена нижче в звичайній логіці claim кнопки
  // Тому тут повертаємо null, щоб не показувати кнопку тут
  // Але якщо квест не завершений, теж не показуємо нічого (claim кнопка буде в другому блоці тільки для завершеного квесту)
  return null;
}

  return null;
}
